import { ApolloProvider } from "@apollo/client";
import { ThemeProvider, StyledEngineProvider } from "@mui/material";
import React, { useContext } from "react";
import ReactDOM from "react-dom";
import setupAplloClient from "./apollo/index";
import App from "./App";
import { ConfigurationProvider } from "./context/Configuration";
import ConfigurationContext from "./context/Configuration";
import { LocationProvider } from "./context/Location";
import { UserProvider } from "./context/User";
import { OrderModeProvider } from "./context/OrderMode";
import { TimeProvider } from "./context/Time";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
// Registers all translation resources with i18next. This used to only ever
// run as a side effect of LanguageCard importing it — the one place in the
// app that referenced this file — so hiding that component silently broke
// every t() call everywhere, not just on Settings.
import "./i18n";
import reportWebVitals from "./reportWebVitals";
import theme from "./utils/theme";
import { perfMark } from "./utils/perfDebug";
import { registerServiceWorker } from "./serviceWorkerRegistration";
import { squash } from "./utils/menuLinks";

// Reads clientId from ConfigurationContext so it's available after config loads
function GoogleAuthWrapper({ children }) {
  const configuration = useContext(ConfigurationContext);
  return (
    <GoogleOAuthProvider clientId={configuration?.webClientID || ""}>
      {children}
    </GoogleOAuthProvider>
  );
}

function Main() {
  const client = setupAplloClient();
  return (
    <HelmetProvider>
      <ApolloProvider client={client}>
        <ConfigurationProvider>
          <GoogleAuthWrapper>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <UserProvider>
                  <LocationProvider>
                    <OrderModeProvider>
                      <TimeProvider>
                        <App />
                      </TimeProvider>
                    </OrderModeProvider>
                  </LocationProvider>
                </UserProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </GoogleAuthWrapper>
        </ConfigurationProvider>
      </ApolloProvider>
    </HelmetProvider>
  );
}
// Old #/ links: emails already sent, printed QR codes, partner iframes and the
// restaurant app carry app.rushmyfood.com/#/restaurant/x?menu=y. The app used
// to route on the hash and now routes on the path, so move the hash route into
// the path before React mounts. Query params from before the hash and inside
// it are merged (?embed=1&restaurant=x#/restaurant/x; a Stripe return adds its
// params to either side), the in-hash ones winning.
function upgradeHashUrl() {
  const { search, hash } = window.location;
  if (!hash.startsWith("#/")) return;
  const q = hash.indexOf("?");
  const route = q >= 0 ? hash.slice(1, q) : hash.slice(1);
  const params = new URLSearchParams(search);
  new URLSearchParams(q >= 0 ? hash.slice(q + 1) : "").forEach((value, key) => params.set(key, value));
  const query = params.toString();
  window.history.replaceState(null, "", `${route}${query ? `?${query}` : ""}`);
}

// First path segment of every route in App.js. Anything else is a short embed
// link. A route missing here still works (an unmatched key leaves the URL for
// the router), it just pays for one embedSlug lookup first.
const APP_ROUTES = new Set([
  "carts", "catering", "change-email", "change-phone", "checkout", "deals",
  "delete-account", "email-sent", "favourite", "forgot-password", "get-help",
  "home-3d", "login", "login-email", "membership", "my-coupons", "my-tickets",
  "new-login", "new-password", "nmi", "order-detail", "orders", "partner-signup",
  "payment-methods", "paypal", "phone-number", "pickup", "privacy", "profile",
  "promotional-coupons", "reactivate", "registration", "reservations", "reserve",
  "restaurant", "restaurant-list", "rewards", "settings", "stripe", "support",
  "terms", "ticket-detail", "verify-email", "verify-forgot-otp", "verify-phone",
  "wallet",
]);

// Short embed links: app.rushmyfood.com/sagesfusionkitchen, or one of its menus:
// app.rushmyfood.com/sagesfusionkitchen/catering. Swap it for the full embed
// URL before React mounts, so Embed.js reads it exactly as a long link.
async function resolveShortEmbedLink() {
  const m = window.location.pathname.match(/^\/([^/.]+)(?:\/([^/.]+))?\/?$/);
  if (!m || APP_ROUTES.has(m[1])) return;
  // Straight to one menu: /capeannpizza/catering → ?menu=catering, which the
  // restaurant page matches to its menus (utils/menuLinks.js).
  const menuKey = m[2] ? squash(m[2]) : "";
  let slug = null;
  try {
    const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:8001/";
    const res = await fetch(`${serverUrl}graphql`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: "query EmbedSlug($key: String!) { embedSlug(key: $key) }",
        variables: { key: decodeURIComponent(m[1]) },
      }),
    });
    slug = (await res.json())?.data?.embedSlug || null;
  } catch (e) {
    // Network or bad escape — fall through to the normal home page.
  }
  // No match: the router's catch-all sends it home.
  if (!slug) return;
  const params = new URLSearchParams(window.location.search);
  params.set("embed", "1");
  params.set("restaurant", slug);
  if (menuKey) params.set("menu", menuKey);
  window.history.replaceState(null, "", `/restaurant/${encodeURIComponent(slug)}?${params}`);
}

// Everything before this point is bundle download, parse and compile.
perfMark("bundle parsed, React starting");
upgradeHashUrl();
resolveShortEmbedLink().finally(() => {
  ReactDOM.render(<Main />, document.getElementById("root"));
});
registerServiceWorker();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
